import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom'
import { Button, Card, CardHeader, CardContent, Input } from '@agms/ui'
import { useAuth, usePermissions } from './hooks/useAuth'
import { apiErrorMessage, apiErrorStatus, isApiError } from './hooks/useErrorHandler'
import { Layout } from './components/Layout'
import { DashboardPage } from './pages/DashboardPage'
import { LeadsPage } from './pages/LeadsPage'
import { AccountPage } from './pages/AccountPage'
import { MyAppointmentsPage } from './pages/MyAppointmentsPage'
import { NewLeadPage } from './pages/NewLeadPage'
import { TurboAppPage } from './pages/TurboAppPage'
import { DeploymentCenterPage } from './pages/DeploymentCenterPage'
import { MerchantsPage } from './pages/MerchantsPage'
import { AccountGroupDetailPage, AccountGroupsPage } from './pages/AccountGroupsPage'
import { ChargebacksPage } from './pages/ChargebacksPage'
import { TicketsPage } from './pages/TicketsPage'
import { TICKET_STATS_FORBIDDEN_MESSAGE, TicketStatsPage } from './pages/TicketStatsPage'
import { HELP_GAPS_FORBIDDEN_MESSAGE, HelpKnowledgeGapsPage } from './pages/HelpKnowledgeGapsPage'
import { HELP_HEALTH_FORBIDDEN_MESSAGE, HelpArticleHealthPage } from './pages/HelpArticleHealthPage'
import {
  ArticleDetailPage,
  ArticleEditorPage,
  ArticleReviewQueuePage,
  HelpCategoriesPage,
  HelpHubPage,
} from './pages/HelpHubPages'
import { SettingsPage } from './pages/SettingsPage'
import { SettingsInterchangeReviewPage } from './pages/SettingsInterchangeReviewPage'
import { SettingsPricingTemplatesPage } from './pages/SettingsPricingTemplatesPage'
import { SettingsScheduleAPage } from './pages/SettingsScheduleAPage'
import { StatementAnalysisDetailPage } from './pages/StatementAnalysisDetailPage'
import { StatementAnalysisPage } from './pages/StatementAnalysisPage'
import { SettingsTicketTypesPage } from './pages/SettingsTicketTypesPage'
import { TicketTypeEditorPage } from './pages/TicketTypeEditorPage'
import { SettingsUserFormPage, SettingsUsersPage } from './pages/SettingsUsersPage'
import { FirstLoginPage } from './pages/FirstLoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { PasswordResetPage } from './pages/PasswordResetPage'
import { SetupTwoFactorPage } from './pages/SetupTwoFactorPage'
import { MerchantDocumentUploadPage } from './pages/MerchantDocumentUploadPage'
import { ReportsPage } from './pages/ReportsPage'
import { FiservReturnUploadPage } from './pages/FiservReturnUploadPage'
import { PageHeader, SurfaceCard } from './components/ui'
import { DocumentTitle } from './lib/documentTitle'
import agmsLogo from './assets/logos/agms-logo-on-white.svg'
import { twoFactorApi } from './lib/api'
import { queryClient } from './lib/queryClient'

/**
 * Protected route wrapper - redirects to login if not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, error } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!user) {
    const status = apiErrorStatus(error)
    const data = isApiError(error) ? error.response?.data : undefined
    const needsEnrollment = data && 'needs_2fa_enrollment' in data
      ? data.needs_2fa_enrollment === true
      : false
    if (status === 423 && needsEnrollment) {
      return <Navigate to="/setup-2fa" replace />
    }
    return <Navigate to="/login" replace />
  }

  return <Layout>{children}</Layout>
}

export function TicketStatsRoute() {
  const { canViewTickets } = usePermissions()

  if (!canViewTickets()) {
    return (
      <p className="text-sm text-rose-700" role="alert">{TICKET_STATS_FORBIDDEN_MESSAGE}</p>
    )
  }

  return <TicketStatsPage />
}

export function HelpKnowledgeGapsRoute() {
  const { hasRole } = usePermissions()

  if (!hasRole(['management', 'super_admin'])) {
    return (
      <p className="text-sm text-rose-700" role="alert">{HELP_GAPS_FORBIDDEN_MESSAGE}</p>
    )
  }

  return <HelpKnowledgeGapsPage />
}

export function HelpArticleHealthRoute() {
  const { hasRole } = usePermissions()

  if (!hasRole(['management', 'super_admin'])) {
    return (
      <p className="text-sm text-rose-700" role="alert">{HELP_HEALTH_FORBIDDEN_MESSAGE}</p>
    )
  }

  return <HelpArticleHealthPage />
}

/**
 * Login page
 */
export function Login() {
  const { loginAsync, isLoggingIn, loginError, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [useRecovery, setUseRecovery] = useState(false)
  const [rememberDevice, setRememberDevice] = useState(false)
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  // Redirect to dashboard once GET /api/user confirms the cookie session.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    document.title = 'Sign in · AGMS CRM'
  }, [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string

    const result = await loginAsync({ email, password })
    if (result.status === 'needs_2fa' && result.challenge_token) {
      setChallengeToken(result.challenge_token)
      setTwoFactorError(null)
      return
    }
    if (result.status === 'needs_2fa_enrollment') {
      navigate('/setup-2fa', { replace: true })
      return
    }
  }

  const handleTwoFactorSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!challengeToken) return

    const formData = new FormData(e.currentTarget)
    const value = String(formData.get(useRecovery ? 'recovery_code' : 'code') ?? '')
    setIsVerifying(true)
    setTwoFactorError(null)

    try {
      await (useRecovery
        ? twoFactorApi.verifyRecovery(challengeToken, value, rememberDevice)
        : twoFactorApi.verifyLogin(challengeToken, value, rememberDevice))
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setTwoFactorError(apiErrorMessage(err, 'Invalid two-factor code.'))
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md overflow-hidden">
        <div className="h-1 bg-accent-500" />
        <CardHeader className="items-center text-center">
          <img src={agmsLogo} alt="AGMS" className="h-12 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Sign in</h1>
        </CardHeader>
        <CardContent>
          {challengeToken ? (
            <form className="space-y-4" onSubmit={handleTwoFactorSubmit}>
              <Input
                key={useRecovery ? 'recovery_code' : 'code'}
                name={useRecovery ? 'recovery_code' : 'code'}
                label={useRecovery ? 'Recovery code' : 'Authenticator code'}
                autoComplete="one-time-code"
                inputMode={useRecovery ? 'text' : 'numeric'}
                required
                placeholder={useRecovery ? 'Enter recovery code' : '123456'}
              />
              <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(event) => setRememberDevice(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  <span className="block font-medium text-gray-900">Remember this device for 14 days</span>
                  <span className="block text-xs text-gray-500">Still requires your password. Skips only the 2FA prompt on this browser.</span>
                </span>
              </label>
              {twoFactorError && (
                <div className="text-sm text-red-600">{twoFactorError}</div>
              )}
              <Button type="submit" className="w-full" isLoading={isVerifying}>
                Verify
              </Button>
              <button
                type="button"
                onClick={() => setUseRecovery(!useRecovery)}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800"
              >
                {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
              </button>
            </form>
          ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input
              name="email"
              label="Email address"
              type="email"
              autoComplete="email"
              required
              placeholder="you@agms.com"
            />
            <Input
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              placeholder="Enter your password"
            />
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
                Forgot password?
              </Link>
            </div>
            {loginError && (
              <div className="text-sm text-red-600">
                {apiErrorMessage(loginError, 'Login failed. Please try again.')}
              </div>
            )}
            <Button type="submit" className="w-full" isLoading={isLoggingIn}>
              Sign in
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </main>
  )
}

/**
 * Phase-labeled placeholder pages for routes not yet implemented.
 * Each page shows its delivery phase so navigation feels intentional.
 */
const PLACEHOLDER_PHASES: Record<string, { phase: string; description: string }> = {
  Pipeline: {
    phase: 'Phase 1',
    description: 'Visual pipeline board with drag-and-drop stage transitions. The lead pipeline data is already flowing — this page will surface it as a Kanban-style board.',
  },
  Merchants: {
    phase: 'Phase 2A',
    description: 'Portfolio dashboard for boarded merchants. Transaction sync, volume metrics, chargeback tracking, and risk tiering — all fed by the Fiserv integration built in Sprint 3.',
  },
  Inbox: {
    phase: 'Phase 4',
    description: 'Universal inbox across all 5 channels (Messenger, Instagram, WhatsApp, SMS, web chat). AI intake collects non-sensitive fields from prospects and writes them to the lead record in real time.',
  },
  Settings: {
    phase: 'Phase 1',
    description: 'User management, role assignments, pricing template editor, and system configuration. RBAC roles are already enforced on every API endpoint.',
  },
}

function PlaceholderPage({ title }: { title: string }) {
  const info = PLACEHOLDER_PHASES[title]
  return (
    <div className="space-y-6">
      <PageHeader title={title} />
      <SurfaceCard>
        <div className="space-y-3 py-12 text-center">
          <div className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
            Coming in {info?.phase ?? 'a future phase'}
          </div>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
            {info?.description ?? 'This feature is planned for a future release.'}
          </p>
        </div>
      </SurfaceCard>
    </div>
  )
}

export function NotFoundPage() {
  useEffect(() => {
    document.title = 'Page not found · AGMS CRM'
  }, [])

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Page not found</h1>
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-blue-600 hover:text-blue-800">
          Back to dashboard
        </Link>
      </div>
    </main>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/first-login" element={<FirstLoginPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/setup-2fa" element={<SetupTwoFactorPage />} />
          <Route path="/upload/:token" element={<FiservReturnUploadPage />} />
          <Route path="/upload/documents/:token" element={<MerchantDocumentUploadPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <LeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/new"
            element={
              <ProtectedRoute>
                <NewLeadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <ProtectedRoute>
                <AccountPage anchorKind="lead" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <MyAppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:id/application"
            element={
              <ProtectedRoute>
                <TurboAppPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline"
            element={
              <ProtectedRoute>
                <DeploymentCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account-groups"
            element={
              <ProtectedRoute>
                <AccountGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account-groups/:id"
            element={
              <ProtectedRoute>
                <AccountGroupDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchants"
            element={
              <ProtectedRoute>
                <MerchantsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchants/:id"
            element={
              <ProtectedRoute>
                <AccountPage anchorKind="merchant" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chargebacks"
            element={
              <ProtectedRoute>
                <ChargebacksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chargebacks/:id"
            element={
              <ProtectedRoute>
                <ChargebacksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/stats"
            element={
              <ProtectedRoute>
                <TicketStatsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <ProtectedRoute>
                <TicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute>
                <TicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpHubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/new"
            element={
              <ProtectedRoute>
                <ArticleEditorPage mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/review"
            element={
              <ProtectedRoute>
                <ArticleReviewQueuePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/categories"
            element={
              <ProtectedRoute>
                <HelpCategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/gaps"
            element={
              <ProtectedRoute>
                <HelpKnowledgeGapsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/health"
            // Registered before /help/:slug. Article slugs do not use "health".
            element={
              <ProtectedRoute>
                <HelpArticleHealthRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/:slug"
            element={
              <ProtectedRoute>
                <ArticleDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/:slug/edit"
            element={
              <ProtectedRoute>
                <ArticleEditorPage mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <PlaceholderPage title="Inbox" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users"
            element={
              <ProtectedRoute>
                <SettingsUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users/new"
            element={
              <ProtectedRoute>
                <SettingsUserFormPage mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users/:id"
            element={
              <ProtectedRoute>
                <SettingsUserFormPage mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statement-analysis"
            element={
              <ProtectedRoute>
                <StatementAnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statement-analysis/:id"
            element={
              <ProtectedRoute>
                <StatementAnalysisDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/pricing-templates"
            element={
              <ProtectedRoute>
                <SettingsPricingTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/schedule-a"
            element={
              <ProtectedRoute>
                <SettingsScheduleAPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/interchange-reviews"
            element={
              <ProtectedRoute>
                <SettingsInterchangeReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/ticket-types"
            element={
              <ProtectedRoute>
                <SettingsTicketTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/ticket-types/new"
            element={
              <ProtectedRoute>
                <TicketTypeEditorPage mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/ticket-types/:id"
            element={
              <ProtectedRoute>
                <TicketTypeEditorPage mode="edit" />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <DocumentTitle />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
